import { useEffect, useState } from 'react';
import { categoryLinesApi, suppliersApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useConfirm } from '../utils/useConfirm';
import { toast } from './Toast';
import Icon from './Icon';
import StatInfo from './StatInfo';
import { normName } from '../utils/normName';

const emails = (people) => people.map(person => person.email).join(', ') || 'אין';

export const HQ_ONLY_EXPLANATION = 'מיועד לספקים רגישים שצריכים להישאר ברמת הבעלים והמטה. ההגבלה חלה בכל המסכים, הדוחות והייצואים.';

export const hasUnmatchedSupplierName = (suppliers, name) => Boolean(
  normName(name) && !suppliers.some(supplier => normName(supplier.name) === normName(name)),
);

export default function NewSupplierOptions({
  name = '',
  hqOnly = false,
  onHqOnlyChange,
  categoryLineId = '',
  onCategoryLineChange,
  showCategory = true,
  compact = false,
  // Edit forms: the supplier being edited (excluded from its own duplicate
  // matches) and whether it is hidden RIGHT NOW in the database — un-hiding a
  // saved hidden supplier reveals its history, so that one direction confirms.
  supplierId = null,
  persistedHqOnly = false,
  // ח.פ. typed or read off the invoice — also matched for likely duplicates.
  businessId = '',
}) {
  const { isAdmin } = useAuth();
  const { budgetModuleEnabled } = useSettings();
  const [lines, setLines] = useState([]);
  const [askConfirm, confirmDialog] = useConfirm();

  useEffect(() => {
    if (!isAdmin || !budgetModuleEnabled || !showCategory) return;
    let cancelled = false;
    categoryLinesApi.getAll()
      .then(response => { if (!cancelled) setLines(response.data || []); })
      .catch(() => { if (!cancelled) toast('שגיאה בטעינת הקטגוריות', 'error'); });
    return () => { cancelled = true; };
  }, [isAdmin, budgetModuleEnabled, showCategory]);

  if (!isAdmin) return null;

  const impact = () => suppliersApi.hqOnlyImpact(name, { supplierId, businessId });

  // Turning it OFF on a saved hidden supplier shows its whole history to staff.
  // Turning it off on a new or unsaved form reveals nothing, so no dialog there.
  const requestReveal = async () => {
    try {
      const { data } = await impact();
      askConfirm({
        title: 'להציג את הספק למנהלים וצופים?',
        body: [
          `כל ההיסטוריה של הספק (מסמכים, תשלומים, זיכויים וסכומים) תוצג ל: ${emails(data.hidden)} (מנהלים, צופים).`,
          'השינוי ייכנס לתוקף מיד לאחר השמירה.',
        ],
        confirmLabel: 'הצג את הספק',
        tone: 'neutral',
        onConfirm: () => onHqOnlyChange(false),
      });
    } catch (error) {
      toast(error?.response?.data?.error || 'לא ניתן לבדוק למי תהיה גישה לספק', 'error');
    }
  };

  const requestHqOnly = async (checked) => {
    if (!checked) {
      if (persistedHqOnly) { requestReveal(); return; }
      onHqOnlyChange(false);
      return;
    }
    try {
      const { data } = await impact();
      const warnings = [];
      // Hiding is per supplier row: a visible twin of this supplier in the same
      // restaurant would still show the same consultant to staff.
      if (data.same_restaurant_matches?.length) {
        warnings.push(`ייתכן שזה אותו ספק, והוא יישאר גלוי: ${data.same_restaurant_matches.map(m => m.name).join(', ')}. כדאי למזג או להסתיר גם אותו.`);
      }
      if (!data.visible.length) warnings.push('אין בעלים/מטה במסעדה — רק Peak יראה את הספק.');
      if (data.same_name_branches.length) {
        warnings.push(`בסניפים הבאים קיים ספק גלוי באותו שם: ${data.same_name_branches.map(branch => branch.name).join(', ')}.`);
      }
      askConfirm({
        title: 'להגביל את הספק לבעלים/מטה בלבד?',
        body: [
          'מנהלים וצופים לא יוכלו לראות את הספק ואת הנתונים המשויכים אליו.',
          'בעלים/מטה ימשיכו לראות את הספק ולעבוד מולו.',
          'ההגבלה תיכנס לתוקף לאחר השמירה, בכל טעינה או פעולה חדשה. מסך שכבר פתוח אצל משתמש אחר יתעדכן ברענון או במעבר הבא.',
          `ימשיכו לראות: ${emails(data.visible)} (בעלים/מטה)`,
          `הגישה תוסר עבור: ${emails(data.hidden)} (מנהלים, צופים)`,
          ...warnings,
        ],
        confirmLabel: 'הגבל לבעלים/מטה',
        tone: 'neutral',
        onConfirm: () => onHqOnlyChange(true),
      });
    } catch (error) {
      toast(error?.response?.data?.error || 'לא ניתן לבדוק למי תהיה גישה לספק', 'error');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: compact ? 'row' : 'column', gap: compact ? 16 : 10, alignItems: compact ? 'end' : 'stretch', flexWrap: 'wrap', marginTop: 10 }}>
      <div className={`supplier-access-setting${compact ? ' supplier-access-setting--compact' : ''}`}>
        <div className="supplier-access-setting-heading">
          <span>גישה לספק</span>
          <StatInfo label="גישה לספק" ariaLabel="למה להגביל את הגישה לספק?">
            {HQ_ONLY_EXPLANATION}
          </StatInfo>
        </div>
        <label className="supplier-access-setting-toggle">
          <input
            type="checkbox"
            checked={!!hqOnly}
            onChange={event => requestHqOnly(event.target.checked)}
          />
          <Icon name="lock" size={16} />
          <span>בעלים/מטה בלבד</span>
        </label>
        <div className="supplier-access-setting-help">
          מנהלים וצופים לא יראו את הספק או את נתוניו.
        </div>
      </div>
      {showCategory && budgetModuleEnabled && (
        <label style={{ minWidth: 190, fontWeight: 500 }}>
          קטגוריה
          <select
            className="form-control"
            value={categoryLineId || ''}
            onChange={event => onCategoryLineChange(event.target.value)}
            style={{ marginTop: 4 }}
          >
            <option value="">— ללא —</option>
            {lines.map(line => <option key={line.id} value={line.id}>{line.name}</option>)}
          </select>
        </label>
      )}
      {confirmDialog}
    </div>
  );
}
